// File Name: pages/match-pages/LiveMatchPage.js
//
// /live — the public, read-only scoreboard for a shared match link.
//
// A web port of the app's LiveScoreboardScreen. Score, goals, team names, head
// counts, status and pause all update live; the clock ticks locally but is
// always recomputed from the server's fields, never incremented.
//
// Read-only by design: no controls, no writes, and no roster listing — the app
// shows every player's name, but a public web page has no business publishing
// the full turnout. Head counts only, plus the scorers who are already part of
// the match record.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  findMatchByCode,
  normaliseCode,
  sideForCode,
  watchGoals,
  watchMatch,
  watchPlayers,
} from '../../lib/ynoMatchDb';
import { isSideB } from '../../lib/matchTime';
import { extraTimeLabel, isOver, statusFor, timerText } from './matchClock';
import { useMatchLang } from './matchI18n';
import { GetAppCard, MatchShell } from './MatchShell';

/** Ticks once a second so the clock advances between Firestore updates. */
function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

export default function LiveMatchPage() {
  const { lang, t, toggle } = useMatchLang();
  const [params] = useSearchParams();

  const code = normaliseCode(params.get('code') || params.get('c') || '');
  const idParam = params.get('id');

  const [matchId, setMatchId] = useState(idParam || null);
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [phase, setPhase] = useState(idParam ? 'loading' : code ? 'resolving' : 'empty');
  const [error, setError] = useState('');
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  // Resolve ?code= to a match id. ?id= skips this.
  useEffect(() => {
    if (matchId || !code) return;
    let dead = false;
    (async () => {
      try {
        const found = await findMatchByCode(code);
        if (dead) return;
        if (!found) {
          setPhase('notfound');
          return;
        }
        setMatchId(found.id);
        setMatch(found);
        setPhase('ready');
      } catch (e) {
        if (!dead) {
          setError(t('couldNotLookup'));
          setPhase('notfound');
        }
      }
    })();
    return () => { dead = true; };
  }, [code, matchId, t]);

  // Live subscriptions.
  useEffect(() => {
    if (!matchId) return undefined;
    const onErr = () => setError(t('connectionLost'));

    const unsubMatch = watchMatch(
      matchId,
      (m) => {
        if (!m) {
          setPhase('notfound');
          return;
        }
        setMatch(m);
        setError('');
        setPhase('ready');
      },
      onErr
    );
    const unsubPlayers = watchPlayers(matchId, setPlayers, onErr);
    const unsubGoals = watchGoals(matchId, setGoals, onErr);

    return () => {
      unsubMatch();
      unsubPlayers();
      unsubGoals();
    };
  }, [matchId, t]);

  const live = match && match.status === 'live';
  const now = useNow(Boolean(live));

  const counts = useMemo(() => {
    let a = 0;
    let b = 0;
    for (const p of players) {
      if (isSideB(p.team)) b += 1;
      else a += 1;
    }
    return { a, b };
  }, [players]);

  const teamName = (side) =>
    (side === 'b' ? match.teamBName : match.teamAName) ||
    (side === 'b' ? 'Team B' : 'Team A');

  // ---- empty / not found -------------------------------------------------
  if (phase === 'empty' || phase === 'notfound') {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <div className="card center">
          <div style={{ fontSize: 44, lineHeight: 1.2 }}>📺</div>
          <h1 style={{ marginTop: 14 }}>
            {phase === 'notfound' ? t('noMatchForCode') : t('noMatchSelected')}
          </h1>
          <p className="sub">{error || t('openShareLink')}</p>
          <Link className="secondary" to="/join" style={{ marginTop: 18 }}>
            {t('backToJoin')}
          </Link>
        </div>
        <GetAppCard t={t} />
      </MatchShell>
    );
  }

  // ---- loading -----------------------------------------------------------
  if (!match) {
    return (
      <MatchShell t={t} lang={lang} toggle={toggle}>
        <div className="card center">
          <div className="spinner" />
          <p className="sub">{t('loadingMatch')}</p>
        </div>
      </MatchShell>
    );
  }

  // ---- the scoreboard ----------------------------------------------------
  const status = statusFor(match);
  const timer = timerText(match, now, t('halfTime'));
  // "90+10" when the host sent a level match back out for extra time. Shown
  // after the whistle too: how a level game got settled is part of the result.
  const extraTime = extraTimeLabel(match);
  const over = isOver(match);
  const side = code ? sideForCode(match, code) : null;
  const feed = goals.slice().reverse(); // newest first

  return (
    <MatchShell t={t} lang={lang} toggle={toggle}>
      <div className="card">
        <div className="pillRow">
          <span className={`dot ${status.tone}`} />
          <span className="statusLabel">{t(status.key)}</span>
        </div>

        {timer ? <div className="timer ltr">{timer}</div> : null}

        {extraTime ? (
          <p className="eyebrow center" style={{ marginTop: 6 }}>
            {t('extraTime')} · <span className="ltr">{extraTime}</span>
          </p>
        ) : null}

        {match.name ? (
          <h1 className="center" style={{ marginTop: 12, fontSize: 22 }}>
            {match.name}
          </h1>
        ) : null}

        <div className="scoreRow">
          <div className="teamCol">
            <div className="badge">A</div>
            <div className="teamName">{teamName('a')}</div>
            <div className="teamCount">
              {counts.a} {counts.a === 1 ? t('player') : t('players')}
            </div>
          </div>

          <div className="score ltr">
            {match.scoreA ?? 0} : {match.scoreB ?? 0}
          </div>

          <div className="teamCol">
            <div className="badge">B</div>
            <div className="teamName">{teamName('b')}</div>
            <div className="teamCount">
              {counts.b} {counts.b === 1 ? t('player') : t('players')}
            </div>
          </div>
        </div>

        {/* Which side this code would put you on, worded exactly as the app
            words it. Pointless once the match is over. */}
        {side && !over ? (
          <div className="note">
            {t('youWillJoin')} <b>{teamName(side)}</b>
          </div>
        ) : null}

        {error ? <p className="err">{error}</p> : null}
      </div>

      <p className="eyebrow" style={{ marginTop: 20 }}>
        {t('goals')}
      </p>
      {feed.length === 0 ? (
        <p className="sub" style={{ marginBottom: 18, fontSize: 13 }}>
          {t('noGoals')}
        </p>
      ) : (
        <div style={{ marginBottom: 9 }}>
          {feed.map((g) => (
            <div className="goalRow" key={g.id}>
              <div className="goalMin ltr">{g.minute ?? 0}&#39;</div>
              <div className="goalIcon">⚽</div>
              <div style={{ minWidth: 0 }}>
                <div className="goalWho">{g.scorerName || '—'}</div>
                <div className="goalSub">
                  {t('goal')} · {teamName(isSideB(g.team) ? 'b' : 'a')}
                  {g.assistName ? ` · ${t('assist')} ${g.assistName}` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <GetAppCard t={t} />
    </MatchShell>
  );
}
